import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Stack,
  Image,
} from "@chakra-ui/react";
import { FaChevronLeft } from "react-icons/fa";
import { ChevronRightIcon } from "@chakra-ui/icons";
import type { CreativeBlock } from "./guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "./types";
import type { BudgetData } from "./components/GuidedBudgetEditor";
import CreativeBlockReview from "./components/CreativeBlockReview";
import {
  CreateCampaignReviewDetailRow,
  reviewDetailRowKey,
  type ReviewDetailRow,
  type ReviewEditorSection,
} from "./components/CreateCampaignReviewDetailRow";
import type { AudienceData } from "common/interfaces/KYDMarketing";
import type { LaunchCampaignTargetingWire } from "@common/interfaces/adsManagerGuidedEdit";
import { hasManualTargetingSliceExcludingStrategies } from "@common/utils/launchTargetingSignal";
import { formatCampaignScheduleWallRange } from "./utils/eventWallTime";
import moneyIcon from "./assets/reviewIcons/money.svg";
import userCircleIcon from "./assets/reviewIcons/user-circle.svg";
import calendarDotsIcon from "./assets/reviewIcons/calendar-dots.svg";
import usersFourIcon from "./assets/reviewIcons/users-four.svg";
import mapPinIcon from "./assets/reviewIcons/map-pin.svg";

export type { ReviewEditorSection } from "./components/CreateCampaignReviewDetailRow";

function formatBudget(budget: BudgetData): string {
  if (budget?.type === "scheduled") {
    const segs = budget.segments ?? [];
    const n = segs.length;
    if (n === 0) return "—";
    const total = segs.reduce((sum, s) => {
      const raw = s.amount?.amount;
      const n = raw != null ? Number(raw) : NaN;
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    const currency = segs[0]?.amount?.currency ?? "USD";
    const symbol = currency === "USD" ? "$" : `${currency} `;
    if (total <= 0) return "—";
    return `${symbol}${Math.round(total)} across ${n} phase${
      n === 1 ? "" : "s"
    } (Meta lifetime + schedules)`;
  }
  const raw = budget?.amount?.amount as number | string | null | undefined;
  const amount =
    raw == null || (typeof raw === "string" && raw.trim() === "")
      ? NaN
      : typeof raw === "number"
      ? raw
      : Number(String(raw).trim());
  const type = budget?.type === "daily" ? "Daily" : "Lifetime";
  if (!Number.isFinite(amount) || amount <= 0) return "—";
  const currency = budget?.amount?.currency ?? "USD";
  const symbol = currency === "USD" ? "$" : `${currency} `;
  return `${symbol}${Math.round(amount)} ${type} Budget`;
}

function audienceDisplayName(audience: AudienceData, fallback: string): string {
  if (audience.name?.trim()) return audience.name.trim();
  if ("description" in audience && audience.description?.trim()) {
    return audience.description.trim();
  }
  return fallback;
}

function formatAudienceType(audience: AudienceData): string {
  const audienceTypeMap = {
    kyd: "KYD",
    csv: "CSV",
    page_lookalike: "Page Lookalike",
    page_engagers: "Page Engagers",
    meta: "Meta",
    interests: "Meta Interests",
  };
  return audienceTypeMap[audience.audience_type] || "Audience";
}

export type CreateCampaignReviewProps = {
  /** Event from draft (Algolia event object) */
  event: {
    name?: string;
    display_start_at?: string;
    venue?: { name?: string; address?: string };
    image?: string;
  } | null;
  adAccountName: string;
  budget: BudgetData;
  scheduleStart: string;
  scheduleEnd: string;
  /** IANA timezone for interpreting schedule wall times */
  scheduleEventTimezone: string;
  locationLabel?: string;
  targeting: {
    strategy: "template" | "custom";
    resolved_strategies:
      | { id?: string; name?: string; description?: string }[]
      | AudienceData[];
    /**
     * Smart Targeting off: org strategies the user enabled (`enabled_strategy_ids`), resolved from
     * {@link CreateCampaignFlow} defaults `manual_targeting_strategy_options`. Shown on review like template rows.
     */
    enabled_manual_targeting_strategies?: {
      id: string;
      name: string;
      description: string;
    }[];
  } & Partial<
    Pick<
      LaunchCampaignTargetingWire,
      | "custom_audiences"
      | "interests"
      | "locations"
      | "demographics"
      | "enabled_strategy_ids"
    >
  >;
  creatives: CreativeBlock[];
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  onBackToEditor: (section?: ReviewEditorSection) => void;
  onLaunch: () => void;
  launching: boolean;
  launchLabel?: string;
  launchLoadingText?: string;
  sectionVisibility?: {
    budget: boolean;
    audience: boolean;
  };
  /** True while Facebook/Instagram identity data is still loading. */
  isIdentityDataLoading?: boolean;
  /** @deprecated Use isIdentityDataLoading. */
  isInstagramAccountsLoading?: boolean;
  partnerPostsEnabled?: boolean;
};

const EVENT_IMAGE_SIZE = "80px";

export default function CreateCampaignReview({
  event,
  adAccountName,
  budget,
  scheduleStart,
  scheduleEnd,
  scheduleEventTimezone,
  locationLabel,
  targeting,
  creatives,
  pages,
  instagramAccounts,
  onBackToEditor,
  onLaunch,
  launching,
  launchLabel = "Launch Campaign",
  launchLoadingText = "Launching…",
  sectionVisibility = { budget: true, audience: true },
  isIdentityDataLoading = false,
  isInstagramAccountsLoading = false,
  partnerPostsEnabled = false,
}: CreateCampaignReviewProps) {
  const eventName = event?.name ?? "Event";
  const eventDate = event?.display_start_at ?? "—";
  const venueLine = event?.venue?.address ?? event?.venue?.name ?? "—";
  const durationDisplay = formatCampaignScheduleWallRange(
    scheduleStart,
    scheduleEnd,
    scheduleEventTimezone
  );
  const locationDisplay = locationLabel ?? venueLine;

  const enabledManualTargetingStrategies =
    targeting.enabled_manual_targeting_strategies ?? [];
  const customAudienceRows: AudienceData[] =
    targeting.strategy === "custom" &&
    Array.isArray(targeting.resolved_strategies) &&
    targeting.resolved_strategies.length > 0 &&
    "audience_type" in (targeting.resolved_strategies[0] ?? {})
      ? (targeting.resolved_strategies as AudienceData[])
      : [];
  const hasManualStrategyList =
    targeting.strategy === "custom" &&
    enabledManualTargetingStrategies.length > 0;
  const hasSyntheticCustomAdSet =
    targeting.strategy === "custom" &&
    hasManualTargetingSliceExcludingStrategies({
      strategy: "custom",
      custom_audiences: targeting.custom_audiences,
      interests: targeting.interests,
      locations: targeting.locations,
      demographics: targeting.demographics,
      enabled_strategy_ids: targeting.enabled_strategy_ids,
    });
  const manualReviewAdSetCount =
    enabledManualTargetingStrategies.length +
    (hasSyntheticCustomAdSet ? 1 : 0);
  const audienceSummaryLabel =
    targeting.strategy === "template"
      ? "Smart Targeting"
      : enabledManualTargetingStrategies.length > 0
        ? "Strategies & targeting"
        : "Custom Targeting";

  const reviewDetailRows: ReviewDetailRow[] = [
    {
      kind: "simple",
      iconSrc: userCircleIcon,
      label: "Ad Account",
      value: adAccountName,
      edit: false,
    },
  ];
  if (sectionVisibility.budget) {
    reviewDetailRows.push({
      kind: "simple",
      iconSrc: calendarDotsIcon,
      label: "Duration",
      value: durationDisplay,
      edit: true,
      section: "budget",
    });
    if (budget?.type === "scheduled") {
      reviewDetailRows.push({ kind: "scheduled_budget" });
    } else {
      reviewDetailRows.push({
        kind: "simple",
        iconSrc: moneyIcon,
        label: "Budget",
        value: formatBudget(budget),
        edit: true,
        section: "budget",
      });
    }
  }
  if (sectionVisibility.audience) {
    reviewDetailRows.push({
      kind: "simple",
      iconSrc: usersFourIcon,
      label: "Audience",
      value: audienceSummaryLabel,
      edit: true,
      section: "audience",
    });
  }
  reviewDetailRows.push({
    kind: "simple",
    iconSrc: mapPinIcon,
    label: "Location",
    value: locationDisplay,
    edit: false,
  });
  const isLaunchDisabled = isIdentityDataLoading || isInstagramAccountsLoading;

  return (
    <Stack
      direction={{ base: "column", md: "row" }}
      align="stretch"
      spacing={6}
      w="full"
      flex={1}
      minH={0}
    >
      {/* Left panel – Campaign Review */}
      <Box
        flex={1}
        bg="#FAFAFA"
        rounded="xl"
        maxW={{ base: "100%", md: "50%" }}
        minW={0}
        shadow="sm"
        borderWidth="1px"
        borderColor="gray.200"
        overflow="auto"
        display="flex"
        flexDirection="column"
      >
        <Box p={6} flex={1}>
          <Text fontSize="xl" fontWeight="bold" color="gray.800" mb={6}>
            Campaign Review
          </Text>

          {/* Campaign Event */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="#DDD"
            rounded="16px"
            p={6}
          >
            <VStack align="stretch" spacing={3}>
              <HStack align="center" justify="space-between">
                <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                  Campaign Event
                </Text>
                <Button
                  variant="link"
                  fontSize="sm"
                  fontWeight="normal"
                  color="blue.600"
                  textDecoration="underline"
                  onClick={() => onBackToEditor("general")}
                  isDisabled={launching}
                  _hover={{ textDecoration: "underline" }}
                >
                  Change
                </Button>
              </HStack>
              <HStack align="center" justify="space-between" spacing={3}>
                <VStack align="stretch" spacing={0} flex={1} minW={0}>
                  <Text
                    fontSize="16px"
                    fontWeight="semibold"
                    letterSpacing="-0.03em"
                    lineHeight="1.2"
                    color="#222"
                    noOfLines={1}
                  >
                    {eventName}
                  </Text>
                  <Text
                    fontSize="14px"
                    color="rgba(34,34,34,0.9)"
                    fontWeight="semibold"
                    noOfLines={1}
                  >
                    {eventDate}
                  </Text>
                  <Text
                    fontSize="12px"
                    color="rgba(34,34,34,0.9)"
                    noOfLines={1}
                  >
                    {venueLine}
                  </Text>
                </VStack>
                {event?.image ? (
                  <Image
                    src={event.image}
                    alt=""
                    w={EVENT_IMAGE_SIZE}
                    h={EVENT_IMAGE_SIZE}
                    objectFit="cover"
                    rounded="md"
                    bg="gray.100"
                    boxShadow="0px 0px 10px 0px rgba(0,0,0,0.2)"
                    flexShrink={0}
                  />
                ) : (
                  <Box
                    w={EVENT_IMAGE_SIZE}
                    h={EVENT_IMAGE_SIZE}
                    rounded="md"
                    bg="gray.200"
                    flexShrink={0}
                  />
                )}
              </HStack>
            </VStack>

            {/* Campaign details list */}
            <VStack align="stretch" spacing={3} mt={3}>
              {reviewDetailRows.map((row) => (
                <CreateCampaignReviewDetailRow
                  key={reviewDetailRowKey(row)}
                  row={row}
                  budget={budget}
                  scheduleEventTimezone={scheduleEventTimezone}
                  onBackToEditor={onBackToEditor}
                  launching={launching}
                />
              ))}
            </VStack>
          </Box>

          {/* Audience Targeting Strategy */}
          <Box
            mt={4}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            rounded="xl"
            p={4}
          >
            <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={2}>
              Audience Targeting Strategy
            </Text>
            {targeting.strategy === "template" ? (
              <Text fontSize="sm" color="gray.600" mb={3} lineHeight="1.4">
                KYD automatically finds your best audience using event data and
                Meta AI across {targeting.resolved_strategies.length} ad sets:
              </Text>
            ) : hasManualStrategyList ? (
              <Text fontSize="15px" color="blackAlpha.700" mb={3} lineHeight="1.4">
                Based on your selected targeting, the following{" "}
                {manualReviewAdSetCount} ad set
                {manualReviewAdSetCount === 1 ? "" : "s"} will be created:
              </Text>
            ) : (
              <Text fontSize="sm" color="gray.600" mb={3} lineHeight="1.4">
                Based on your selected targeting, the following ad set(s) will
                be created:
              </Text>
            )}
            <VStack align="stretch" spacing={0} pl={0}>
              {targeting.strategy === "template" ? (
                <>
                  {targeting.resolved_strategies.map((audience, i) => (
                    <Box
                      key={audience.id ?? i}
                      borderTop="1px solid"
                      borderColor="gray.200"
                      py={2}
                    >
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        fontWeight="semibold"
                        lineHeight="1.3"
                      >
                        {audience.name}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.700"
                        lineHeight="1.4"
                        mt={1}
                      >
                        {audience.description}
                      </Text>
                    </Box>
                  ))}
                </>
              ) : hasManualStrategyList ? (
                <>
                  {enabledManualTargetingStrategies.map((s) => (
                    <Box
                      key={s.id}
                      borderTop="1px solid"
                      borderColor="gray.200"
                      py={2}
                    >
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        fontWeight="semibold"
                        lineHeight="1.3"
                      >
                        {s.name}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.700"
                        lineHeight="1.4"
                        mt={1}
                      >
                        {s.description}
                      </Text>
                    </Box>
                  ))}
                  {hasSyntheticCustomAdSet ? (
                    <Box borderTop="1px solid" borderColor="gray.200" py={2}>
                      <HStack justify="space-between" align="flex-start" w="full">
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          fontWeight="semibold"
                          lineHeight="1.3"
                        >
                          Custom Audience
                        </Text>
                        <Button
                          variant="link"
                          fontSize="sm"
                          fontWeight="normal"
                          color="blue.600"
                          textDecoration="underline"
                          onClick={() => onBackToEditor("audience")}
                          isDisabled={launching}
                          _hover={{ textDecoration: "underline" }}
                          p={0}
                          h="auto"
                          minW="unset"
                        >
                          Edit
                        </Button>
                      </HStack>
                      {customAudienceRows.length > 0 ? (
                        <VStack align="start" spacing={0} mt={1}>
                          {customAudienceRows.map((audience, idx) => (
                            <Text
                              fontSize="sm"
                              color="gray.700"
                              lineHeight="1.4"
                              key={idx}
                            >
                              {audienceDisplayName(
                                audience,
                                `Audience ${idx + 1}`
                              )}{" "}
                              ({formatAudienceType(audience)})
                            </Text>
                          ))}
                        </VStack>
                      ) : null}
                    </Box>
                  ) : null}
                </>
              ) : (
                <>
                  <Box borderTop="1px solid" borderColor="gray.200" py={2}>
                    <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                      Custom Audience
                    </Text>
                    <VStack align="start" spacing={0}>
                      {(targeting.resolved_strategies as AudienceData[]).map(
                        (audience, idx) => (
                          <Text fontSize="xs" color="gray.500" key={idx}>
                            {audienceDisplayName(
                              audience,
                              `Audience ${idx + 1}`
                            )}{" "}
                            ({formatAudienceType(audience)})
                          </Text>
                        )
                      )}
                    </VStack>
                  </Box>
                </>
              )}
            </VStack>
          </Box>
        </Box>

        {/* Footer buttons */}
        <HStack
          justify="space-between"
          w="full"
          p={6}
          pt={4}
          borderTopWidth="1px"
          borderColor="gray.100"
        >
          <Button
            variant="outline"
            size="md"
            bg="gray.100"
            borderColor="gray.300"
            color="gray.800"
            leftIcon={<FaChevronLeft style={{ marginRight: "4px" }} />}
            onClick={() => onBackToEditor("general")}
            _hover={{ bg: "gray.200" }}
            isDisabled={launching}
          >
            Back
          </Button>
          <Button
            colorScheme="green"
            size="md"
            rightIcon={<ChevronRightIcon />}
            onClick={onLaunch}
            isLoading={launching}
            isDisabled={isLaunchDisabled}
            loadingText={launchLoadingText}
          >
            {launchLabel}
          </Button>
        </HStack>
      </Box>

      {/* Right panel – Ad Media */}
      <Box
        flex={1}
        maxW={{ base: "100%", md: "50%" }}
        minW={0}
        bg="#FAFAFA"
        rounded="xl"
        shadow="sm"
        borderWidth="1px"
        borderColor="gray.200"
        overflow="auto"
        p={6}
      >
        <Text fontSize="xl" fontWeight="bold" color="gray.800" mb={6}>
          Ad Media
        </Text>

        <VStack align="stretch" spacing={8}>
          {creatives.map((creative, index) => (
            <CreativeBlockReview
              key={index}
              creative={creative}
              index={index}
              onEdit={() => onBackToEditor("creative")}
              launching={launching}
              pages={pages}
              instagramAccounts={instagramAccounts}
              partnerPostsEnabled={partnerPostsEnabled}
            />
          ))}
        </VStack>
      </Box>
    </Stack>
  );
}
